import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Video, Plus, ArrowRight, Users, MessageSquare, Phone, PhoneOff, ExternalLink, Copy, Check, LogOut } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// توليد اسم غرفة فريد
const generateRoomName = () => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `alkhayat-${timestamp}-${random}`;
};

export default function Meetings() {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [currentMeeting, setCurrentMeeting] = useState(null);
  const [copied, setCopied] = useState(false);
  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    meeting_type: 'internal',
    meeting_mode: 'video',
    participants: [],
    participant_names: [],
    scheduled_time: '',
    duration_minutes: 60,
    meeting_link: '',
    notes: '',
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchData();
    
    // تحميل Jitsi Meet API
    const script = document.createElement('script');
    script.src = 'https://meet.jit.si/external_api.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose();
      }
    };
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [meetingsRes, employeesRes, clientsRes] = await Promise.all([
        axios.get(`${API}/meetings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/employees`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setMeetings(meetingsRes.data);
      setEmployees(employeesRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // إنشاء رابط Jitsi تلقائياً إذا كان الاجتماع فيديو
      let meetingLink = formData.meeting_link;
      if (formData.meeting_mode === 'video' && !meetingLink) {
        const roomName = generateRoomName();
        meetingLink = `https://meet.jit.si/${roomName}`;
      }
      
      await axios.post(`${API}/meetings`, {
        ...formData,
        meeting_link: meetingLink
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setShowModal(false);
      fetchData();
      setFormData({
        title: '',
        meeting_type: 'internal',
        meeting_mode: 'video',
        participants: [],
        participant_names: [],
        scheduled_time: '',
        duration_minutes: 60,
        meeting_link: '',
        notes: '',
      });
    } catch (err) {
      console.error('Error creating meeting:', err);
    }
  };

  const handleParticipantToggle = (personId, personName) => {
    const isSelected = formData.participants.includes(personId);
    if (isSelected) {
      setFormData({
        ...formData,
        participants: formData.participants.filter((id) => id !== personId),
        participant_names: formData.participant_names.filter((name) => name !== personName),
      });
    } else {
      setFormData({
        ...formData,
        participants: [...formData.participants, personId],
        participant_names: [...formData.participant_names, personName],
      });
    }
  };

  const startVideoCall = (meeting) => {
    setCurrentMeeting(meeting);
    setShowVideoCall(true);
    
    // انتظار تحميل الـ container
    setTimeout(() => {
      if (jitsiContainerRef.current && window.JitsiMeetExternalAPI) {
        // استخراج اسم الغرفة من الرابط
        let roomName = meeting.meeting_link?.split('/').pop() || generateRoomName();
        
        const domain = 'meet.jit.si';
        const options = {
          roomName: roomName,
          width: '100%',
          height: '100%',
          parentNode: jitsiContainerRef.current,
          userInfo: {
            displayName: user?.full_name || 'مستخدم',
          },
          configOverwrite: {
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            prejoinPageEnabled: false,
            disableDeepLinking: true,
          },
          interfaceConfigOverwrite: {
            TOOLBAR_BUTTONS: [
              'microphone', 'camera', 'closedcaptions', 'desktop', 'fullscreen',
              'fodeviceselection', 'hangup', 'chat', 'recording',
              'livestreaming', 'etherpad', 'sharedvideo', 'settings', 'raisehand',
              'videoquality', 'filmstrip', 'invite', 'feedback', 'stats', 'shortcuts',
              'tileview', 'videobackgroundblur', 'download', 'help', 'mute-everyone',
            ],
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            DEFAULT_BACKGROUND: '#1a1a2e',
            DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
          },
        };
        
        jitsiApiRef.current = new window.JitsiMeetExternalAPI(domain, options);
        
        // معالجة انتهاء المكالمة
        jitsiApiRef.current.addListener('readyToClose', () => {
          endVideoCall();
        });
      }
    }, 100);
  };

  const endVideoCall = () => {
    if (jitsiApiRef.current) {
      jitsiApiRef.current.dispose();
      jitsiApiRef.current = null;
    }
    setShowVideoCall(false);
    setCurrentMeeting(null);
  };

  const copyMeetingLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback للمتصفحات القديمة
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        alert('الرابط: ' + link);
      }
      document.body.removeChild(textArea);
    }
  };

  const startInstantMeeting = () => {
    const roomName = generateRoomName();
    const meetingLink = `https://meet.jit.si/${roomName}`;
    
    setCurrentMeeting({
      title: 'اجتماع فوري',
      meeting_link: meetingLink
    });
    setShowVideoCall(true);
    
    setTimeout(() => {
      if (jitsiContainerRef.current && window.JitsiMeetExternalAPI) {
        const domain = 'meet.jit.si';
        const options = {
          roomName: roomName,
          width: '100%',
          height: '100%',
          parentNode: jitsiContainerRef.current,
          userInfo: {
            displayName: user?.full_name || 'مستخدم',
          },
          configOverwrite: {
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            prejoinPageEnabled: false,
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
          },
        };
        
        jitsiApiRef.current = new window.JitsiMeetExternalAPI(domain, options);
        jitsiApiRef.current.addListener('readyToClose', endVideoCall);
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">الاجتماعات</h1>
          </div>
          <div className="flex gap-3">
            {/* زر اجتماع فوري */}
            <button
              data-testid="instant-meeting-btn"
              onClick={startInstantMeeting}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-all duration-300"
            >
              <Video className="w-5 h-5" />
              اجتماع فوري
            </button>
            {user?.role !== 'client' && (
              <button
                data-testid="add-meeting-btn"
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all duration-300"
              >
                <Plus className="w-5 h-5" />
                جدولة اجتماع
              </button>
            )}
            <button
              onClick={() => {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                navigate('/login');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              خروج
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* بطاقة معلومات */}
        <div className="bg-gradient-to-l from-blue-500 to-blue-600 rounded-xl p-6 mb-8 text-white">
          <div className="flex items-center gap-4">
            <Video className="w-12 h-12" />
            <div>
              <h2 className="text-xl font-heading font-bold mb-1">مكالمات فيديو مجانية</h2>
              <p className="text-blue-100 font-body">يمكنك بدء اجتماع فوري أو جدولة اجتماع مستقبلي. الخدمة مدعومة من Jitsi Meet</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {meetings.map((meeting) => (
            <div
              key={meeting.id}
              className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              data-testid={`meeting-card-${meeting.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  {meeting.meeting_mode === 'video' ? (
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Video className="w-6 h-6 text-blue-600" />
                    </div>
                  ) : (
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <MessageSquare className="w-6 h-6 text-purple-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-heading font-semibold text-primary">
                        {meeting.title}
                      </h3>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-body">
                        {meeting.meeting_mode === 'video' ? 'فيديو' : 'كتابي'}
                      </span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-body">
                        {meeting.meeting_type === 'internal' ? 'داخلي' : 'مع عميل'}
                      </span>
                    </div>
                    <div className="space-y-1 mb-3">
                      <p className="text-sm text-gray-600 font-body">
                        <strong>الموعد:</strong>{' '}
                        {new Date(meeting.scheduled_time).toLocaleString('ar-EG', {
                          dateStyle: 'full',
                          timeStyle: 'short',
                        })}
                      </p>
                      <p className="text-sm text-gray-600 font-body">
                        <strong>المدة:</strong> {meeting.duration_minutes} دقيقة
                      </p>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <p className="text-sm text-gray-600 font-body">
                          {meeting.participant_names?.join('، ') || 'لا يوجد مشاركين'}
                        </p>
                      </div>
                    </div>
                    
                    {/* أزرار الإجراءات */}
                    {meeting.meeting_mode === 'video' && meeting.meeting_link && (
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => startVideoCall(meeting)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-all text-sm font-body"
                          data-testid={`join-meeting-${meeting.id}`}
                        >
                          <Phone className="w-4 h-4" />
                          انضم للاجتماع
                        </button>
                        <button
                          onClick={() => copyMeetingLink(meeting.meeting_link)}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-all text-sm font-body"
                        >
                          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          نسخ الرابط
                        </button>
                        <a
                          href={meeting.meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-all text-sm font-body"
                        >
                          <ExternalLink className="w-4 h-4" />
                          فتح في تبويب جديد
                        </a>
                      </div>
                    )}
                    
                    {meeting.notes && (
                      <p className="text-sm text-gray-500 mt-3 font-body bg-gray-50 p-2 rounded">{meeting.notes}</p>
                    )}
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-body whitespace-nowrap ${
                    meeting.status === 'scheduled'
                      ? 'bg-yellow-100 text-yellow-700'
                      : meeting.status === 'completed'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {meeting.status === 'scheduled'
                    ? 'مجدول'
                    : meeting.status === 'completed'
                    ? 'مكتمل'
                    : meeting.status}
                </span>
              </div>
            </div>
          ))}
        </div>

        {meetings.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl" data-testid="no-meetings">
            <Video className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-body mb-4">لا توجد اجتماعات مجدولة</p>
            <button
              onClick={startInstantMeeting}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-all font-body"
            >
              ابدأ اجتماع فوري الآن
            </button>
          </div>
        )}
      </main>

      {/* نافذة مكالمة الفيديو */}
      {showVideoCall && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          {/* شريط التحكم */}
          <div className="bg-gray-900 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3 text-white">
              <Video className="w-6 h-6 text-green-500" />
              <span className="font-body">{currentMeeting?.title || 'اجتماع فيديو'}</span>
            </div>
            <div className="flex items-center gap-3">
              {currentMeeting?.meeting_link && (
                <button
                  onClick={() => copyMeetingLink(currentMeeting.meeting_link)}
                  className="flex items-center gap-2 px-3 py-1 bg-gray-700 text-white rounded text-sm font-body hover:bg-gray-600"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  نسخ رابط الدعوة
                </button>
              )}
              <button
                onClick={endVideoCall}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-all font-body"
                data-testid="end-call-btn"
              >
                <PhoneOff className="w-5 h-5" />
                إنهاء المكالمة
              </button>
            </div>
          </div>
          
          {/* حاوية Jitsi */}
          <div ref={jitsiContainerRef} className="flex-1" />
        </div>
      )}

      {/* نافذة جدولة اجتماع */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 my-8" data-testid="meeting-modal">
            <h2 className="text-2xl font-heading font-bold text-primary mb-6">
              جدولة اجتماع جديد
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  عنوان الاجتماع
                </label>
                <input
                  data-testid="meeting-title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  placeholder="مثال: اجتماع فريق القضايا"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    نوع الاجتماع
                  </label>
                  <select
                    data-testid="meeting-type"
                    value={formData.meeting_type}
                    onChange={(e) => setFormData({ ...formData, meeting_type: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  >
                    <option value="internal">داخلي (موظفين)</option>
                    <option value="client">مع عميل</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    وضع الاجتماع
                  </label>
                  <select
                    data-testid="meeting-mode"
                    value={formData.meeting_mode}
                    onChange={(e) => setFormData({ ...formData, meeting_mode: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  >
                    <option value="video">فيديو كول (Jitsi)</option>
                    <option value="chat">كتابي (دردشة)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    الموعد
                  </label>
                  <input
                    data-testid="meeting-time"
                    type="datetime-local"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    المدة (دقيقة)
                  </label>
                  <input
                    data-testid="meeting-duration"
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  المشاركين
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-4 space-y-2">
                  {formData.meeting_type === 'internal'
                    ? employees.map((emp) => (
                        <label key={emp.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={formData.participants.includes(emp.id)}
                            onChange={() => handleParticipantToggle(emp.id, emp.full_name)}
                            className="w-4 h-4 text-gold focus:ring-gold"
                          />
                          <span className="font-body">{emp.full_name} - {emp.role === 'lawyer' ? 'محامي' : emp.role === 'accountant' ? 'محاسب' : 'موظف'}</span>
                        </label>
                      ))
                    : clients.map((client) => (
                        <label key={client.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={formData.participants.includes(client.id)}
                            onChange={() => handleParticipantToggle(client.id, client.full_name)}
                            className="w-4 h-4 text-gold focus:ring-gold"
                          />
                          <span className="font-body">{client.full_name}</span>
                        </label>
                      ))}
                  {(formData.meeting_type === 'internal' ? employees : clients).length === 0 && (
                    <p className="text-gray-400 text-sm font-body text-center py-4">لا يوجد {formData.meeting_type === 'internal' ? 'موظفين' : 'عملاء'}</p>
                  )}
                </div>
              </div>

              {formData.meeting_mode === 'video' && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-700 font-body">
                    <strong>ملاحظة:</strong> سيتم إنشاء رابط اجتماع Jitsi تلقائياً عند الحفظ. يمكنك مشاركة الرابط مع المشاركين.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  ملاحظات
                </label>
                <textarea
                  data-testid="meeting-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  placeholder="جدول الأعمال أو ملاحظات إضافية..."
                />
              </div>

              <div className="flex gap-4 justify-end">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-body"
                >
                  إلغاء
                </button>
                <button
                  data-testid="meeting-submit"
                  type="submit"
                  className="px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all font-body"
                >
                  جدولة
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
